import { useCallback, useEffect, useState } from 'react';
import {
  listarRegistroHoras,
  calcularHorasAcademico,
  crearRegistroHoras,
  actualizarRegistroHoras,
  cerrarRegistroHoras,
  reabrirRegistroHoras,
} from '../../../../services/registroHorasService';
import { showToast } from '../../../../utils/showToast';

const hoy = new Date();

export default function useRegistroHorasLogic() {
  const [mes,  setMes]  = useState(hoy.getMonth() + 1);
  const [anio, setAnio] = useState(hoy.getFullYear());

  const [registros, setRegistros] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);

  // Modal de edición
  const [modal,      setModal]      = useState(false);
  const [editRow,    setEditRow]    = useState(null);   // fila completa del docente
  const [formHoras,  setFormHoras]  = useState({ horas_definitivas: '', observaciones: '' });
  const [saving,     setSaving]     = useState(false);
  const [calculando, setCalculando] = useState(false);

  const fetchRegistros = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listarRegistroHoras(mes, anio);
      setRegistros(Array.isArray(data) ? data : []);
      setError(null);
    } catch (err) {
      const msg = err.message || 'No se pudo obtener el registro de horas.';
      setError(msg);
      showToast({ type: 'danger', message: msg });
    } finally {
      setLoading(false);
    }
  }, [mes, anio]);

  useEffect(() => { fetchRegistros(); }, [fetchRegistros]);

  // ── Abrir modal de edición/creación ──────────────────────────────────────
  const abrirEditar = useCallback((row) => {
    setEditRow(row);
    setFormHoras({
      horas_definitivas: row.horas_definitivas ?? row.horas_calculadas ?? '',
      observaciones:     row.observaciones ?? '',
    });
    setModal(true);
  }, []);

  const cerrarModal = useCallback(() => {
    setModal(false);
    setEditRow(null);
  }, []);

  // ── Calcular desde sistema académico ────────────────────────────────────
  const handleCalcular = useCallback(async (row) => {
    setCalculando(row.empleado_id);
    try {
      const res = await calcularHorasAcademico(row.empleado_id, mes, anio);
      const horas = res.horas_calculadas ?? 0;

      if (row.registro_id) {
        // Ya existe → sólo actualiza horas_calculadas en formulario si está abierto
        setRegistros((prev) =>
          prev.map((r) =>
            r.empleado_id === row.empleado_id
              ? { ...r, horas_calculadas: horas }
              : r
          )
        );
        showToast({ type: 'info', message: `Sistema académico: ${horas} h. Edita y guarda para confirmar.` });
      } else {
        // No existe → crear registro automáticamente
        const nuevo = await crearRegistroHoras({
          empleado_id:       row.empleado_id,
          mes,
          anio,
          horas_calculadas:  horas,
          horas_definitivas: horas,
        });
        setRegistros((prev) =>
          prev.map((r) => r.empleado_id === row.empleado_id ? { ...r, ...flattenRegistro(nuevo) } : r)
        );
        showToast({ type: 'success', message: `Calculado y guardado: ${horas} horas.` });
      }
    } catch (err) {
      showToast({ type: 'danger', message: err.message || 'Error al calcular horas.' });
    } finally {
      setCalculando(null);
    }
  }, [mes, anio]);

  // ── Guardar (crear o actualizar) ─────────────────────────────────────────
  const handleGuardar = useCallback(async () => {
    if (formHoras.horas_definitivas === '' || formHoras.horas_definitivas === null) {
      showToast({ type: 'warning', message: 'Ingresa las horas definitivas.' });
      return;
    }
    setSaving(true);
    try {
      let updated;
      if (editRow.registro_id) {
        updated = await actualizarRegistroHoras(editRow.registro_id, {
          horas_definitivas: parseFloat(formHoras.horas_definitivas),
          observaciones:     formHoras.observaciones || null,
        });
      } else {
        updated = await crearRegistroHoras({
          empleado_id:       editRow.empleado_id,
          mes,
          anio,
          horas_calculadas:  editRow.horas_calculadas ?? 0,
          horas_definitivas: parseFloat(formHoras.horas_definitivas),
          observaciones:     formHoras.observaciones || null,
        });
      }
      setRegistros((prev) =>
        prev.map((r) =>
          r.empleado_id === editRow.empleado_id ? { ...r, ...flattenRegistro(updated) } : r
        )
      );
      showToast({ type: 'success', message: 'Registro guardado.' });
      cerrarModal();
    } catch (err) {
      showToast({ type: 'danger', message: err.message || 'Error al guardar.' });
    } finally {
      setSaving(false);
    }
  }, [editRow, formHoras, mes, anio, cerrarModal]);

  // ── Cerrar registro ───────────────────────────────────────────────────────
  const handleCerrar = useCallback(async (row) => {
    if (!row.registro_id) {
      showToast({ type: 'warning', message: 'Primero debe existir un registro para cerrarlo.' });
      return;
    }
    try {
      const updated = await cerrarRegistroHoras(row.registro_id);
      setRegistros((prev) =>
        prev.map((r) => r.empleado_id === row.empleado_id ? { ...r, ...flattenRegistro(updated) } : r)
      );
      showToast({ type: 'success', message: 'Registro cerrado correctamente.' });
    } catch (err) {
      showToast({ type: 'danger', message: err.message || 'Error al cerrar registro.' });
    }
  }, []);

  // ── Reabrir registro ──────────────────────────────────────────────────────
  const handleReabrir = useCallback(async (row) => {
    if (!row.registro_id) return;
    try {
      const updated = await reabrirRegistroHoras(row.registro_id);
      setRegistros((prev) =>
        prev.map((r) => r.empleado_id === row.empleado_id ? { ...r, ...flattenRegistro(updated) } : r)
      );
      showToast({ type: 'success', message: 'Registro reabierto.' });
    } catch (err) {
      showToast({ type: 'danger', message: err.message || 'Error al reabrir registro.' });
    }
  }, []);

  return {
    mes, setMes, anio, setAnio,
    registros, loading, error, reload: fetchRegistros,
    modal, editRow, formHoras, setFormHoras, saving, calculando,
    abrirEditar, cerrarModal,
    handleCalcular, handleGuardar,
    handleCerrar, handleReabrir,
  };
}

// Aplana un objeto registro_horas (desde el backend) a los campos que usa la fila
function flattenRegistro(r) {
  return {
    registro_id:        r.id,
    mes:                r.mes,
    anio:               r.anio,
    horas_calculadas:   r.horas_calculadas,
    horas_definitivas:  r.horas_definitivas,
    observaciones:      r.observaciones,
    estado:             r.estado,
    cerrado_at:         r.cerrado_at,
    registro_created_at: r.created_at,
  };
}
